/*
 * Copyright (c) 2025-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createAction } from 'helpers/createAction'
import ajax from 'helpers/ajax'
import { SettingsResponse } from 'types/responses/SettingsResponse'
import { toast } from 'utils/toastUtils'
import { TodoCategoryRequest, TodoRequest } from 'types/entities/TodoRequests'
import { TodoCategory } from 'types/entities/TodoCategory'
import { setLanguage } from 'store/session/SessionReducer'
import type { Todo } from 'types/entities/Todo'
import { AppThunkDispatch } from 'helpers/store'
import { FETCH_SETTINGS, SET_SETTINGS } from './SettingsActionTypes'

class SettingsAction {
    static readonly FETCH_SETTINGS = FETCH_SETTINGS
    static readonly SET_SETTINGS = SET_SETTINGS
    static readonly API_CREATE_TICKET = '/admin/tickets/create'
    static readonly API_POST_SETTINGS = '/settings/set'
    static readonly API_POST_ADVERTISEMENT = '/settings/advertisement'
    static readonly API_ACTIVATE_ADVERTISEMENT_PAGE =
        '/settings/advertisement-page'
    static readonly API_CREATE_USER = '/drivers/create'

    static fetchRestartJava() {
        return () => {
            return ajax.get('/admin/system/restart-java').then((r) => {
                toast.success(r)
                return r
            })
        }
    }

    static fetchResetLoops() {
        return () => {
            return ajax.get('/admin/system/reset-loops').then((r) => {
                toast.success(r)
                return r
            })
        }
    }

    static fetchSettings() {
        return (dispatch: AppThunkDispatch) => {
            return ajax.get('/settings/list').then((r: SettingsResponse) => {
                dispatch(SettingsAction.setSettings(r))
                return r
            })
        }
    }

    static fetchPublicSettings() {
        return (dispatch: AppThunkDispatch) => {
            return ajax.get('/settings/public').then((r: SettingsResponse) => {
                dispatch(SettingsAction.setSettings(r))
                return r
            })
        }
    }

    static fetchActivateDisplayAdvertisement(data: {
        page: string
        active: boolean
    }) {
        console.log(data)
        return (dispatch: AppThunkDispatch) => {
            return ajax
                .post(SettingsAction.API_ACTIVATE_ADVERTISEMENT_PAGE, {
                    page: data.page,
                    active: data.active,
                })
                .then((_r) => {
                    dispatch(this.fetchSettings()).then((r) => {
                        console.log(r)
                        return r
                    })
                })
        }
    }

    static postAdvertisementPage(data: any) {
        return (dispatch: AppThunkDispatch) => {
            return ajax
                .post(SettingsAction.API_POST_ADVERTISEMENT, {
                    displayAdvertisements: data,
                })
                .then((_r) => {
                    console.log('postAdvertisement')
                    dispatch(this.fetchSettings()).then((r) => {
                        console.log(r)
                        return r
                    })
                })
        }
    }

    static postSettings(data: any) {
        return (dispatch: AppThunkDispatch) => {
            return ajax
                .post(SettingsAction.API_POST_SETTINGS, data)
                .then((r) => {
                    console.log('postSettings')
                    dispatch(this.fetchSettings()).then((r) => {
                        if (r.config && r.languages) {
                            const selectedLanguage = r.languages.find(
                                (l) => l.code === r.config?.language
                            )
                            if (selectedLanguage) {
                                dispatch(setLanguage(selectedLanguage))
                            }
                        }
                    })
                    return r
                })
        }
    }

    static createUser(data: any) {
        return (dispatch: AppThunkDispatch) => {
            return ajax
                .post(SettingsAction.API_CREATE_USER, data)
                .then((_r) => dispatch(this.fetchSettings()))
        }
    }

    static createTicket(data: any) {
        return (dispatch: AppThunkDispatch) => {
            return ajax
                .post(SettingsAction.API_CREATE_TICKET, data)
                .then((_r) => dispatch(this.fetchSettings()))
        }
    }

    static createTicketWithResponse(data: any) {
        return () => {
            return ajax
                .post(SettingsAction.API_CREATE_TICKET, data)
                .then((r) => {
                    return r
                })
        }
    }

    static createTodo(data: TodoRequest) {
        return () => {
            return ajax
                .post('/settings/todo/create', { ...data })
                .then((r: Todo) => {
                    // dispatch(SettingsAction.setSettings(r));
                    return r
                })
        }
    }

    static updateTodo(todoId: number, data: TodoRequest) {
        return () => {
            return ajax
                .post('/settings/todo/update/' + todoId, { ...data })
                .then((r: Todo) => {
                    // dispatch(SettingsAction.setSettings(r));
                    return r
                })
        }
    }

    static deleteTodo(todoId: number) {
        return () => {
            return ajax
                .delete('/settings/todo/delete/' + todoId)
                .then((r: boolean) => {
                    // dispatch(SettingsAction.setSettings(r));
                    return r
                })
        }
    }

    static createTodoCategory(data: TodoCategoryRequest) {
        return () => {
            return ajax
                .post('/settings/todo/category/create', { ...data })
                .then((r: TodoCategory) => {
                    // dispatch(SettingsAction.setSettings(r));
                    return r
                })
        }
    }

    static updateTodoCategory(
        todoCategoryId: number,
        data: TodoCategoryRequest
    ) {
        return () => {
            return ajax
                .post('/settings/todo/category/update/' + todoCategoryId, {
                    ...data,
                })
                .then((r: TodoCategory) => {
                    // dispatch(SettingsAction.setSettings(r));
                    return r
                })
        }
    }

    static deleteTodoCategory(todoCategoryId: number) {
        return () => {
            return ajax
                .delete('/settings/todo/category/delete/' + todoCategoryId)
                .then((r: boolean) => {
                    // dispatch(SettingsAction.setSettings(r));
                    return r
                })
        }
    }

    static setSettings(settings: SettingsResponse) {
        return createAction(SettingsAction.SET_SETTINGS, settings)
    }
}

export default SettingsAction
